import { Codicon } from 'vscode/vscode/vs/base/common/codicons';
import { URI } from 'vscode/vscode/vs/base/common/uri';
import { localize2WithPath, localizeWithPath } from 'vscode/vscode/vs/nls';
import { Categories } from 'vscode/vscode/vs/platform/action/common/actionCommonCategories';
import { registerAction2, Action2, MenuId, MenuRegistry } from 'vscode/vscode/vs/platform/actions/common/actions';
import { ICommandService } from 'vscode/vscode/vs/platform/commands/common/commands.service';
import { IConfigurationService } from 'vscode/vscode/vs/platform/configuration/common/configuration.service';
import { ContextKeyExpr } from 'vscode/vscode/vs/platform/contextkey/common/contextkey';
import { IQuickInputService } from 'vscode/vscode/vs/platform/quickinput/common/quickInput.service';
import { NOTEBOOK_ACTIONS_CATEGORY } from 'vscode/vscode/vs/workbench/contrib/notebook/browser/controller/coreActions';
import { getNotebookEditorFromEditorPane } from 'vscode/vscode/vs/workbench/contrib/notebook/browser/notebookBrowser';
import { INotebookEditorService } from 'vscode/vscode/vs/workbench/contrib/notebook/browser/services/notebookEditorService.service';
import { NotebookSetting } from 'vscode/vscode/vs/workbench/contrib/notebook/common/notebookCommon';
import { NOTEBOOK_IS_ACTIVE_EDITOR, NOTEBOOK_EDITOR_FOCUSED } from 'vscode/vscode/vs/workbench/contrib/notebook/common/notebookContextKeys';
import { INotebookService } from 'vscode/vscode/vs/workbench/contrib/notebook/common/notebookService.service';
import { IEditorService } from 'vscode/vscode/vs/workbench/services/editor/common/editorService.service';
import { IPreferencesService } from 'vscode/vscode/vs/workbench/services/preferences/common/preferences.service';

const _moduleId = "vs/workbench/contrib/notebook/browser/controller/layoutActions";
registerAction2(class NotebookConfigureLayoutAction extends Action2 {
    constructor() {
        super({
            id: 'workbench.notebook.layout.select',
            title: ( localize2WithPath(_moduleId, 0, "Select between Notebook Layouts")),
            f1: true,
            precondition: ( (ContextKeyExpr.equals(`config.${NotebookSetting.openGettingStarted}`, true))),
            category: NOTEBOOK_ACTIONS_CATEGORY,
            menu: [
                {
                    id: MenuId.EditorTitle,
                    group: 'notebookLayout',
                    when: ( (ContextKeyExpr.and(
                        NOTEBOOK_IS_ACTIVE_EDITOR,
                         (ContextKeyExpr.notEquals('config.notebook.globalToolbar', true)),
                         (ContextKeyExpr.equals(`config.${NotebookSetting.openGettingStarted}`, true))
                    ))),
                    order: 0
                },
                {
                    id: MenuId.NotebookToolbar,
                    group: 'notebookLayout',
                    when: ( (ContextKeyExpr.and(
                         (ContextKeyExpr.equals('config.notebook.globalToolbar', true)),
                         (ContextKeyExpr.equals(`config.${NotebookSetting.openGettingStarted}`, true))
                    ))),
                    order: 0
                }
            ]
        });
    }
    run(accessor) {
        accessor.get(ICommandService).executeCommand('workbench.action.openWalkthrough', { category: 'notebooks', step: 'notebookProfile' }, true);
    }
});
registerAction2(class NotebookConfigureLayoutAction extends Action2 {
    constructor() {
        super({
            id: 'workbench.notebook.layout.configure',
            title: ( localize2WithPath(_moduleId, 1, "Customize Notebook Layout")),
            f1: true,
            category: NOTEBOOK_ACTIONS_CATEGORY,
            menu: [
                {
                    id: MenuId.NotebookToolbar,
                    group: 'notebookLayout',
                    when: ( (ContextKeyExpr.equals('config.notebook.globalToolbar', true))),
                    order: 1
                }
            ]
        });
    }
    run(accessor) {
        accessor.get(IPreferencesService).openSettings({ jsonEditor: false, query: '@tag:notebookLayout' });
    }
});
registerAction2(class NotebookConfigureLayoutFromEditorTitle extends Action2 {
    constructor() {
        super({
            id: 'workbench.notebook.layout.configure.editorTitle',
            title: ( localize2WithPath(_moduleId, 1, "Customize Notebook Layout")),
            f1: false,
            category: NOTEBOOK_ACTIONS_CATEGORY,
            menu: [
                {
                    id: MenuId.NotebookEditorLayoutConfigure,
                    group: 'notebookLayout',
                    when: NOTEBOOK_IS_ACTIVE_EDITOR,
                    order: 1
                }
            ]
        });
    }
    run(accessor) {
        accessor.get(IPreferencesService).openSettings({ jsonEditor: false, query: '@tag:notebookLayout' });
    }
});
MenuRegistry.appendMenuItem(MenuId.EditorTitle, {
    submenu: MenuId.NotebookEditorLayoutConfigure,
    rememberDefaultAction: false,
    title: ( localize2WithPath(_moduleId, 2, "Customize Notebook...")),
    icon: Codicon.gear,
    group: 'navigation',
    order: -1,
    when: NOTEBOOK_IS_ACTIVE_EDITOR
});
registerAction2(class ToggleLineNumberFromEditorTitle extends Action2 {
    constructor() {
        super({
            id: 'notebook.toggleLineNumbersFromEditorTitle',
            title: ( localize2WithPath(_moduleId, 3, 'Toggle Notebook Line Numbers')),
            precondition: NOTEBOOK_EDITOR_FOCUSED,
            menu: [
                {
                    id: MenuId.NotebookEditorLayoutConfigure,
                    group: 'notebookLayoutDetails',
                    order: 1,
                    when: NOTEBOOK_IS_ACTIVE_EDITOR
                }
            ],
            category: NOTEBOOK_ACTIONS_CATEGORY,
            f1: true,
            toggled: {
                condition: ( (ContextKeyExpr.notEquals('config.notebook.lineNumbers', 'off'))),
                title: ( localizeWithPath(_moduleId, 4, "Notebook Line Numbers")),
            }
        });
    }
    async run(accessor) {
        return accessor.get(ICommandService).executeCommand('notebook.toggleLineNumbers');
    }
});
registerAction2(class ToggleCellToolbarPositionFromEditorTitle extends Action2 {
    constructor() {
        super({
            id: 'notebook.toggleCellToolbarPositionFromEditorTitle',
            title: ( localize2WithPath(_moduleId, 5, 'Toggle Cell Toolbar Position')),
            menu: [{
                    id: MenuId.NotebookEditorLayoutConfigure,
                    group: 'notebookLayoutDetails',
                    order: 3
                }],
            category: NOTEBOOK_ACTIONS_CATEGORY,
            f1: false
        });
    }
    async run(accessor, ...args) {
        return accessor.get(ICommandService).executeCommand('notebook.toggleCellToolbarPosition', ...args);
    }
});
registerAction2(class ToggleBreadcrumbFromEditorTitle extends Action2 {
    constructor() {
        super({
            id: 'breadcrumbs.toggleFromEditorTitle',
            title: ( localize2WithPath(_moduleId, 6, 'Toggle Breadcrumbs')),
            menu: [{
                    id: MenuId.NotebookEditorLayoutConfigure,
                    group: 'notebookLayoutDetails',
                    order: 2
                }],
            f1: false
        });
    }
    async run(accessor) {
        return accessor.get(ICommandService).executeCommand('breadcrumbs.toggle');
    }
});
registerAction2(class SaveMimeTypeDisplayOrder extends Action2 {
    constructor() {
        super({
            id: 'notebook.saveMimeTypeOrder',
            title: ( localize2WithPath(_moduleId, 7, "Save Mimetype Display Order")),
            f1: true,
            category: NOTEBOOK_ACTIONS_CATEGORY,
            precondition: NOTEBOOK_IS_ACTIVE_EDITOR,
        });
    }
    run(accessor) {
        const service = accessor.get(INotebookService);
        const qp = accessor.get(IQuickInputService).createQuickPick();
        qp.placeholder = ( localizeWithPath(_moduleId, 8, 'Settings file to save in'));
        qp.items = [
            { target: 2 , label: ( localizeWithPath(_moduleId, 9, 'User Settings')) },
            { target: 5 , label: ( localizeWithPath(_moduleId, 10, 'Workspace Settings')) },
        ];
        qp.onDidAccept(() => {
            const target = qp.selectedItems[0]?.target;
            if (target !== undefined) {
                service.saveMimeDisplayOrder(target);
            }
            qp.dispose();
        });
        qp.onDidHide(() => qp.dispose());
        qp.show();
    }
});
registerAction2(class NotebookWebviewResetAction extends Action2 {
    constructor() {
        super({
            id: 'workbench.notebook.layout.webview.reset',
            title: ( localize2WithPath(_moduleId, 11, "Reset Notebook Webview")),
            f1: false,
            category: NOTEBOOK_ACTIONS_CATEGORY
        });
    }
    run(accessor, args) {
        const editorService = accessor.get(IEditorService);
        if (args) {
            const uri = URI.revive(args);
            const notebookEditorService = accessor.get(INotebookEditorService);
            const widgets = notebookEditorService.listNotebookEditors().filter(widget => widget.hasModel() && ( (widget.textModel.uri.toString())) === ( (uri.toString())));
            for (const widget of widgets) {
                if (widget.hasModel()) {
                    widget.getInnerWebview()?.reload();
                }
            }
        }
        else {
            const editor = getNotebookEditorFromEditorPane(editorService.activeEditorPane);
            if (!editor) {
                return;
            }
            editor.getInnerWebview()?.reload();
        }
    }
});
registerAction2(class ToggleNotebookStickyScroll extends Action2 {
    constructor() {
        super({
            id: 'notebook.action.toggleNotebookStickyScroll',
            title: {
                ...( localize2WithPath(_moduleId, 12, "Toggle Notebook Sticky Scroll")),
                mnemonicTitle: ( localizeWithPath(_moduleId, 13, "&&Toggle Notebook Sticky Scroll")),
            },
            category: Categories.View,
            toggled: {
                condition: ( (ContextKeyExpr.equals('config.notebook.stickyScroll.enabled', true))),
                title: ( localizeWithPath(_moduleId, 14, "Toggle Notebook Sticky Scroll")),
                mnemonicTitle: ( localizeWithPath(_moduleId, 13, "&&Toggle Notebook Sticky Scroll")),
            },
            menu: [
                { id: MenuId.CommandPalette },
                {
                    id: MenuId.NotebookStickyScrollContext,
                    group: 'notebookView',
                    order: 2
                }
            ]
        });
    }
    async run(accessor) {
        const configurationService = accessor.get(IConfigurationService);
        const newValue = !configurationService.getValue('notebook.stickyScroll.enabled');
        return configurationService.updateValue('notebook.stickyScroll.enabled', newValue);
    }
});
